import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature, useLocalize, useWorkspace } from '@treams/treams-hooks';
import { WarningIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Jumbo,
	MultiSelect,
	NumericInput,
	Paragraph,
	Select,
	Separator,
	Spinner,
	tokens,
} from '@treams/treams-ui';

function WorkspaceStartDate(props: { startDate?: Date }) {
	const workspace = useWorkspace();
	const [startDate, setStartDate] = useState(props.startDate);

	const [updateStartDate, { loading }] = graphql.useUpdateWorkspaceStartDateMutation();

	return (
		<Flex alignItems="center">
			<Box width={200} mr={2}>
				<Input.DatePicker
					value={startDate}
					onChange={(startDate) => {
						setStartDate(startDate);

						updateStartDate({
							variables: { input: { workspaceID: workspace.id, startDate } },
						});
					}}
				/>
			</Box>
			{loading && <Spinner />}
		</Flex>
	);
}

const inputOnlyLanguages = ['it_IT', 'es_ES', 'pt_BR', 'da_DK', 'sv_SE', 'pl_PL'];

function WorkspaceLanguages(props: { languageIDs: string[] }) {
	const workspace = useWorkspace();
	const { t } = useTranslation();
	const { meta } = useLocalize();

	const [languageIDs, setLanguageIDs] = useState(props.languageIDs);

	const [updateLanguages, { loading }] = graphql.useUpdateWorkspaceLanguagesMutation();

	const options = _.sortBy(
		meta.treamsLanguages.map((value) => ({
			title: t(`common.language.${value}`),
			value,
		})),
		'title',
	);

	const inputOnlyOptions = languageIDs.filter((languageID) =>
		inputOnlyLanguages.includes(languageID),
	);

	return (
		<Flex alignItems="center">
			<Box mr={2}>
				<MultiSelect
					required
					selectedOptions={options.filter(({ value }) => languageIDs.includes(value))}
					options={options}
					onChange={(languageIDs) => {
						setLanguageIDs(languageIDs);

						updateLanguages({
							variables: { input: { workspaceID: workspace.id, languageIDs } },
						});
					}}
				/>

				{inputOnlyOptions.length > 0 && (
					<Flex mt={2}>
						<Callout
							icon={<WarningIcon />}
							color="warning"
							description={t('settings.advanced.languages.inputOnly', {
								languages: inputOnlyOptions
									.map((locale) => t(`common.language.${locale}`))
									.join(', '),
							})}
						/>
					</Flex>
				)}
			</Box>
			{loading && <Spinner />}
		</Flex>
	);
}

function EngagementSettings(props: { type: graphql.EngagementEnpsType }) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateEngagementSettingsMutation({});

	if (loading) {
		return <Select.Skeleton />;
	}

	return (
		<Select
			search={false}
			onChange={(value) => {
				update({ variables: { input: { enpsType: value as graphql.EngagementEnpsType } } });
			}}
			selectedValue={props.type}
			options={[
				{
					title: t('settings.advanced.enps.european.title'),
					value: graphql.EngagementEnpsType.European,
				},
				{
					title: t('settings.advanced.enps.american.title'),
					value: graphql.EngagementEnpsType.American,
				},
			]}
		/>
	);
}

function RetentionPeriodDays(props: { days?: number | null }) {
	const workspace = useWorkspace();
	const { t } = useTranslation();

	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [days, setDays] = useState(props.days);
	const [update, { loading, error }] = graphql.useUpdateWorkspaceRetentionPeriodMutation({});

	const onSave = async () => {
		try {
			await update({
				variables: { input: { workspaceID: workspace.id, retentionPeriodDays: days! } },
			});
			setHasUnsavedChanges(false);
		} catch {
			// no-op
		}
	};

	return (
		<div>
			<Flex alignItems="baseline" justifyContent="left">
				<Box mr={2} width={65} flexShrink={0}>
					<NumericInput
						tintedIcon={true}
						value={`${days}`}
						onChange={(newDays) => {
							const newDaysInt = parseInt(newDays);
							setHasUnsavedChanges(true);
							setDays(newDaysInt);
						}}
					/>
				</Box>
				<Box pr={2} mr={2}>
					{t('settings.advanced.retentionPeriod.days')}
				</Box>
				<Box pr={2} mr={2}>
					{hasUnsavedChanges && (
						<Button
							disableSuccess={true}
							loading={loading}
							type={'primary'}
							title={t('common.save')}
							onClick={onSave}
						/>
					)}
				</Box>
			</Flex>
			<GraphQLErrorMessage error={error?.graphQLErrors[0]} />
		</div>
	);
}

function WorkspaceSSOSettings(props: { ssoSettings: graphql.WorkspaceSsoSettingsFragment }) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateWorkspaceSsoSettingsMutation({});

	const onChange = useCallback(
		(value: string) => {
			update({
				variables: { input: { enforceSSO: value === 'enabled' } },
			});
		},
		[update],
	);

	if (loading) {
		return <Select.Skeleton />;
	}

	return (
		<Select
			search={false}
			onChange={onChange}
			selectedValue={props.ssoSettings.enforceSSO ? 'enabled' : 'disabled'}
			options={[
				{
					title: t('settings.advanced.sso.option.disabled'),
					value: 'disabled',
				},
				{
					title: t('settings.advanced.sso.option.enabled'),
					value: 'enabled',
				},
			]}
		/>
	);
}

function WorkspaceMfaSettings(props: { mfaSettings: graphql.WorkspaceMfaSettingsFragment }) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateWorkspaceMfaSettingsMutation({});

	const onChange = useCallback(
		(status: graphql.WorkspaceMfaStatus) => {
			update({
				variables: { input: { status, allowedMethods: [graphql.MfaMethod.Totp] } },
			});
		},
		[update],
	);

	if (loading) {
		return <Select.Skeleton />;
	}

	return (
		<Select
			search={false}
			onChange={onChange}
			selectedValue={props.mfaSettings.status}
			options={[
				{
					title: t('settings.advanced.mfa.option.disabled'),
					value: graphql.WorkspaceMfaStatus.Disabled,
				},
				{
					title: t('settings.advanced.mfa.option.enabled'),
					value: graphql.WorkspaceMfaStatus.Enabled,
				},
				{
					title: t('settings.advanced.mfa.option.required'),
					value: graphql.WorkspaceMfaStatus.Required,
				},
			]}
		/>
	);
}

export default function AdvancedSettings() {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const query = graphql.useAdvancedWorkspaceSettingsQuery({ variables: { id: workspace.id } });
	const hasMfa = useFeature(graphql.FeatureName.Mfa);

	return (
		<>
			<Jumbo
				title={t('settings.advanced.title')}
				description={t('settings.advanced.description')}
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<Flex alignItems="center">
					<Box width={300} mr={3}>
						<Heading3>{t('settings.advanced.startDate.title')}</Heading3>
						<Paragraph>{t('settings.advanced.startDate.description')}</Paragraph>
					</Box>
					{query.data ? (
						<WorkspaceStartDate
							startDate={
								typeof query.data.workspace.startDate === 'string'
									? new Date(query.data.workspace.startDate)
									: undefined
							}
						/>
					) : (
						<Box width={200}>
							<Input.Skeleton />
						</Box>
					)}
				</Flex>
				<Separator mt={`${tokens.gap.section}px`} />
				<Flex alignItems="center" mt={`${tokens.gap.section}px`}>
					<Box width={300} mr={3}>
						<Heading3>{t('settings.advanced.languages.title')}</Heading3>
						<Paragraph>{t('settings.advanced.languages.description')}</Paragraph>
					</Box>
					{query.data ? (
						<WorkspaceLanguages languageIDs={query.data.workspace.supportedLanguageIDs!} />
					) : (
						<Box width={200}>
							<Input.Skeleton />
						</Box>
					)}
				</Flex>
				<Separator mt={`${tokens.gap.section}px`} />
				<Flex alignItems="center" mt={`${tokens.gap.section}px`}>
					<Box width={300} mr={3}>
						<Heading3>{t('settings.advanced.enps.title')}</Heading3>
						<Paragraph>{t('settings.advanced.enps.description')}</Paragraph>
					</Box>
					{query.data ? (
						<Box width={200}>
							<EngagementSettings type={query.data.workspace.engagementSettings.enpsType} />
						</Box>
					) : (
						<Box width={200}>
							<Select.Skeleton />
						</Box>
					)}
				</Flex>
				<Separator mt={`${tokens.gap.section}px`} />
				<Flex alignItems="center" mt={`${tokens.gap.section}px`}>
					<Box width={300} mr={3}>
						<Heading3>{t('settings.advanced.retentionPeriod.title')}</Heading3>
						<Paragraph>{t('settings.advanced.retentionPeriod.description')}</Paragraph>
					</Box>
					{query.data ? (
						<Box width={200}>
							<RetentionPeriodDays days={query.data.workspace.retentionPeriodDays} />
						</Box>
					) : (
						<Box width={200}>
							<Select.Skeleton />
						</Box>
					)}
				</Flex>
				{hasMfa && (
					<>
						<Separator mt={`${tokens.gap.section}px`} />
						<Flex alignItems="center" mt={`${tokens.gap.section}px`}>
							<Box width={300} mr={3}>
								<Heading3>{t('settings.advanced.mfa.title')}</Heading3>
								<Paragraph>{t('settings.advanced.mfa.description')}</Paragraph>
							</Box>
							{query.data ? (
								<Box width={200}>
									<WorkspaceMfaSettings mfaSettings={query.data.workspace.workspaceMfaSettings} />
								</Box>
							) : (
								<Box width={200}>
									<Select.Skeleton />
								</Box>
							)}
						</Flex>
					</>
				)}
				<Separator mt={`${tokens.gap.section}px`} />
				<Flex alignItems="center" mt={`${tokens.gap.section}px`}>
					<Box width={300} mr={3}>
						<Heading3>{t('settings.advanced.sso.title')}</Heading3>
						<Paragraph>{t('settings.advanced.sso.description')}</Paragraph>
					</Box>
					<Box width={200}>
						{query.data ? (
							<WorkspaceSSOSettings ssoSettings={query.data.workspace.ssoSettings} />
						) : (
							<Select.Skeleton />
						)}
					</Box>
				</Flex>
			</Container>
		</>
	);
}
